import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Form } from "@/components/ui/form"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { AppDispatch } from "@/redux"
import { useDispatch } from "react-redux"
import { getFormProps } from "@/helpers/formHelpers"
import { OptionProps } from "@/types/workflow"
import { workflowActions } from "@/redux/slice/workflowSlice"
import { useEffect } from "react"
import { TextInputField } from "@/components/custom/InputField/TextInputField"

const formSchema = z.object({
  optionValue: z.string(),
})

export const TextboxOption: React.FC<OptionProps> = ({
  code,
  stepId,
  order,
  selectedValue,
  name,
  description,
  showOptionModal,
  optionModal,
}: OptionProps) => {
  const dispatch: AppDispatch = useDispatch()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      optionValue: ''
    },
  })
  const optionValue = form.watch('optionValue')
  const formProps = getFormProps(form, formSchema)

  useEffect(() => {
    if (selectedValue) {
      form.setValue('optionValue', selectedValue)
    }
  }, [selectedValue, form])

  return (
    <AlertDialog open={optionModal} onOpenChange={() => showOptionModal && showOptionModal(false)}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="mb-6">
            <div className="flex flex-row items-start justify-between">
              <div className="flex flex-col items-start">
                <h4 className="text-primary font-semibold text-xl leading-5 mb-2">
                  Option Setup: {name}
                </h4>
                <p className="text-secondary text-sm mb-2 text-left font-normal">{description}</p>
              </div>
              <div className="flex items-center">
                <AlertDialogCancel className="mx-4">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  type="submit"
                  disabled={optionValue?.length === 0}
                  onClick={() => {
                    if (optionValue?.length > 0) {
                      dispatch(workflowActions.setStepOption({
                        stepId,
                        order,
                        option: code,
                        optionValue
                      }))
                    }
                  }}
                >
                  Update Option
                </AlertDialogAction>
              </div>
            </div>
          </AlertDialogTitle>
          <div className="">
            <div className="my-4" />
            <Form {...form}>
              <form className="space-y-6 flex flex-col items-start">
                <TextInputField
                  name="optionValue"
                  label={name}
                  error={!!form.formState.errors.optionValue}
                  additionalClassName="w-[460px] bg-foreground"
                  {...formProps}
                />
              </form>
            </Form>
          </div>
        </AlertDialogHeader>
      </AlertDialogContent>
    </AlertDialog>
  )
}
